import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowIcon, CopyIcon, DiscordIcon } from '../components/Icons';
import { SectionLabel, TiltCard } from '../components/Effects';
import { EpisodesSection } from '../components/EpisodesSection';
import { NewsSection } from '../components/NewsSection';
import type { SessionUser } from '../types';

export function HomePage({ user, loading }: { user: SessionUser | null; loading: boolean; onUserChange?: (user: SessionUser) => void }) {
  const [copied, setCopied] = useState('');
  const copy = async (value: string, label: string) => { await navigator.clipboard.writeText(value); setCopied(label); setTimeout(() => setCopied(''), 1800); };

  return <>
    <section className="hero">
      <div className="hero-bg" />
      <div className="hero-vignette" />
      <div className="hero-content">
        <div className="eyebrow"><span /> SRI LANKA'S PLAYER-BUILT CIVILIZATION <i>SEASON 3</i></div>
        <img src="/assets/logo.webp" alt="Uge Appage SMP" className="hero-logo" />
        <h1>Forge your name.<br/><em>Build the nation.</em></h1>
        <p>A living Minecraft civilization where every coin, machine, alliance, and legend is made by players. Java and Bedrock—one shared world.</p>
        <HeroActions user={user} loading={loading} copy={copy} copied={copied} />
        <div className="hero-stats">
          <div><b>1B</b><small>LKR sovereign economy</small></div><i/><div><b>16</b><small>Industrial patents</small></div><i/><div><b>∞</b><small>Stories to build</small></div>
        </div>
      </div>
      <div className="scroll-cue"><span>SCROLL TO ENTER</span><i /></div>
    </section>

    <div className="world-ticker" aria-hidden="true"><div>{Array.from({length:2},(_,loop)=><span key={loop}><b>SEASON III</b><i/>PLAYER-BUILT ECONOMY<i/>JAVA + BEDROCK<i/>SOVEREIGN WORLD<i/>16 INDUSTRIAL PATENTS<i/>YOUR STORY STARTS HERE<i/></span>)}</div></div>

    <section className="manifesto section-pad">
      <SectionLabel index="01">THE WORLD</SectionLabel>
      <div className="split-heading" data-reveal>
        <h2>Not another server.<br/><em>A sovereign world.</em></h2>
        <p>Choose a town. Form a faction. Trade, invent, govern, and leave a mark that survives long after the battle ends.</p>
      </div>
      <div className="world-grid">
        <TiltCard className="world-image" ><img src="/assets/world-settlement.webp" alt="Old Town and New Town sovereign valley" loading="lazy" /><div className="image-caption"><span>LIVE WORLD / SEASON III</span><b>Two towns. One unfolding history.</b></div></TiltCard>
        <div className="world-copy" data-reveal>
          <article><span>01</span><div><h3>Old Town</h3><p>Heritage, politics, and deep-rooted power. Build inside a community shaped by its founders.</p></div></article>
          <article><span>02</span><div><h3>New Town</h3><p>A frontier for ambitious citizens—new districts, new industries, new rivalries.</p></div></article>
          <article><span>03</span><div><h3>Faction destiny</h3><p>Create alliances, defend your claim, and turn reputation into lasting influence.</p></div></article>
        </div>
      </div>
    </section>

    <section className="economy-preview section-pad">
      <div className="bank-bg" data-parallax="12" />
      <div className="economy-inner">
        <SectionLabel index="02">THE ECONOMY</SectionLabel>
        <div className="split-heading" data-reveal><h2>Every rupee has<br/><em>a history.</em></h2><p>No infinite stock. No conjured wealth. Every commodity begins in the hands of a real citizen.</p></div>
        <div className="reserve-grid" data-reveal>
          <div className="reserve-number"><small>SOVEREIGN RESERVE</small><b>රු. 1,000,000,000</b><p>200M active circulation <span/> 800M secured reserve</p></div>
          <div className="money-stack" aria-hidden="true"><img src="/assets/note_100000.webp"/><img src="/assets/note_10000.webp"/><img src="/assets/note_1000.webp"/></div>
        </div>
        <div className="feature-row">
          <TiltCard><span>PHYSICAL WEALTH</span><h3>Wallets can be lost.<br/>Fortunes can be stolen.</h3><p>Carry cash beyond safe zones at your own risk. Black money carries its own price.</p></TiltCard>
          <TiltCard><span>TRUE SUPPLY</span><h3>Nothing appears<br/>from thin air.</h3><p>Markets move because citizens mine, farm, craft, and trade.</p></TiltCard>
          <TiltCard><span>STATE LAW</span><h3>Earn. Pay. Or work<br/>off the debt.</h3><p>Progressive tax, bank credit, and a quarry below the State Penitentiary.</p></TiltCard>
        </div>
        <Link className="outline-link" to="/economy">Explore the full economy <ArrowIcon /></Link>
      </div>
    </section>

    <div className="chapter-break" aria-hidden="true"><span>THE INDUSTRIAL AGE</span><i/><b>03</b></div>

    <section className="industry section-pad">
      <SectionLabel index="03">INDUSTRIAL OCEAN YARDS</SectionLabel>
      <div className="split-heading" data-reveal><h2>Industry, exiled<br/><em>to the horizon.</em></h2><p>Heavy engineering lives offshore—where ambitious machines can grow without stealing performance from the mainland.</p></div>

      <div className="industry-yards-grid" data-reveal>
        <TiltCard className="yard-card">
          <div className="yard-card-media">
            <img src="/assets/ocean-yard-old.webp" alt="Old Town Ocean Yard Biosphere Dome" loading="lazy" />
            <span className="yard-badge slime">DR. SLIME · 8 CLASSES</span>
          </div>
          <div className="yard-card-body">
            <span>OFFSHORE BIOSPHERE DOME</span>
            <h3>Old Town Ocean Yard</h3>
            <p>Dr. Slime's oceanic geodesic dome. High-efficiency automated crop processors, mob reactors, and classified slime-tier patents.</p>
            <div className="yard-meta">
              <div><small>ZONE</small><b>Old Town Waters</b></div>
              <div><small>CHUNK LIMIT</small><b>24 Machines</b></div>
              <div><small>PATRON</small><b>Dr. Slime</b></div>
            </div>
          </div>
        </TiltCard>

        <TiltCard className="yard-card">
          <div className="yard-card-media">
            <img src="/assets/ocean-yard-new.webp" alt="New Town Ocean Yard High-Tech Platform" loading="lazy" />
            <span className="yard-badge nautilus">PROF. NAUTILUS · 8 CLASSES</span>
          </div>
          <div className="yard-card-body">
            <span>HIGH-TECH SHIPYARD COMPLEX</span>
            <h3>New Town Ocean Yard</h3>
            <p>Prof. Nautilus's deep-sea industrial platform. Precision quartz engineering, redstone clock hubs, and heavy metallurgical fabrication.</p>
            <div className="yard-meta">
              <div><small>ZONE</small><b>New Town Deep Sea</b></div>
              <div><small>CHUNK LIMIT</small><b>24 Machines</b></div>
              <div><small>PATRON</small><b>Prof. Nautilus</b></div>
            </div>
          </div>
        </TiltCard>
      </div>

      <div className="industry-map" data-reveal>
        <div className="ocean-grid"/><div className="yard old"><i/><span>OLD TOWN YARD</span><b>DR. SLIME</b><small>8 patent classes</small></div>
        <div className="route-line"><i/><i/><i/></div>
        <div className="yard new"><i/><span>NEW TOWN YARD</span><b>PROF. NAUTILUS</b><small>8 patent classes</small></div>
        <div className="map-center"><span>LICENSED INDUSTRY</span><b>24</b><small>MAX MACHINES / CHUNK</small></div>
      </div>
    </section>

    {/* Section 04: The Complete Episodes & Video Archive */}
    <EpisodesSection />

    {/* Section 05: Official Chronicle & News Dispatches */}
    <NewsSection />

    <section className="server-showcase section-pad">
      <SectionLabel index="06">SOVEREIGN ARCHIVE</SectionLabel>
      <div className="split-heading" data-reveal>
        <h2>Real server world.<br/><em>Built by our people.</em></h2>
        <p>Every fortress, canal, arena, and lantern district shown here exists right now inside the living server. No mockups. Real player architecture.</p>
      </div>
      <div className="gallery-showcase-grid" data-reveal>
        <TiltCard className="gallery-card">
          <img src="/assets/colosseum-pvp.webp" alt="Uge Appage Colosseum Arena" loading="lazy" />
          <div className="gallery-card-badge"><span>PVP & TOURNAMENTS</span><b>Colosseum Grand Arena</b></div>
        </TiltCard>
        <TiltCard className="gallery-card">
          <img src="/assets/harbor-town.webp" alt="Uge Appage Grand Server Hub" loading="lazy" />
          <div className="gallery-card-badge"><span>CENTRAL SPAWN</span><b>Uge Appage Grand Hub</b></div>
        </TiltCard>
        <TiltCard className="gallery-card">
          <img src="/assets/night-market.webp" alt="Central Hub Night Trading Pavilion" loading="lazy" />
          <div className="gallery-card-badge"><span>HUB NIGHT PAVILION</span><b>The Hub Night Plaza</b></div>
        </TiltCard>
        <TiltCard className="gallery-card">
          <img src="/assets/cherry-shrine.webp" alt="Cherry Blossom Mountain Estate" loading="lazy" />
          <div className="gallery-card-badge"><span>EASTERN PROVINCE</span><b>Cherry Blossom Pagoda</b></div>
        </TiltCard>
      </div>
    </section>

    <section id="legends" className="legends section-pad">
      <SectionLabel index="07">HALL OF LEGENDS</SectionLabel>
      <div className="split-heading" data-reveal><h2>The world remembers<br/><em>who shaped it.</em></h2><p>Season 3 begins with empty plinths. The first names carved here will belong to citizens who change the world.</p></div>
      <div className="plinths" data-reveal>{['THE ARCHITECT','THE MAGNATE','THE ENGINEER'].map((title, i) => <div key={title}><span>0{i+1}</span><div className="silhouette">?</div><h3>{title}</h3><p>Awaiting a legend</p></div>)}</div>
    </section>

    <section className="crossplay section-pad">
      <img src="/assets/crossplay-portal.webp" alt="Java and Bedrock players entering one shared world" data-parallax="8" loading="lazy" />
      <div className="crossplay-overlay" />
      <div className="crossplay-copy" data-reveal><SectionLabel index="08">HOW TO JOIN</SectionLabel><h2>One realm.<br/><em>Every platform.</em></h2><p>Connect from Java PC, Bedrock Mobile, or Bedrock Windows. Your citizenship begins with Discord.</p>
        <div className="join-steps"><span><b>01</b>Join Discord</span><i/><span><b>02</b>Read the rules</span><i/><span><b>03</b>Apply & interview</span></div>
        <div className="final-actions"><a className="primary-btn enchant" href="https://apply.ugeappagesmp.online">Apply For Whitelist <ArrowIcon /></a><Link className="outline-link" to="/rules">Read official rules <ArrowIcon /></Link></div>
      </div>
    </section>
  </>;
}

function HeroActions({ user, loading, copy, copied }: { user: SessionUser | null; loading: boolean; copy: (value: string, label: string) => void; copied: string }) {
  if (loading) return <div className="hero-actions"><span className="skeleton-btn" /></div>;
  if (!user) return <div className="hero-actions"><a className="primary-btn enchant" href="https://apply.ugeappagesmp.online">✦ Apply for Whitelist <ArrowIcon /></a><a className="ghost-link discord" href="/auth/discord"><DiscordIcon /> Discord Login</a><Link className="outline-link" to="/journey">Our Journey <ArrowIcon /></Link></div>;
  if (!user.inGuild) return <div className="status-card warning"><span>DISCORD REQUIRED</span><h3>Join the community first.</h3><p>Your account is connected, but citizenship requires membership in the official Discord.</p><a className="discord-btn" href={import.meta.env.VITE_DISCORD_INVITE_URL || 'https://discord.gg/hAqvebcSh3'} target="_blank" rel="noreferrer"><DiscordIcon/> Join our Discord</a></div>;
  if (user.status === 'passed') return <div className="citizen-card"><span>✓ WHITELISTED CITIZEN</span><h3>Welcome, {user.mcUsername}</h3><div><button onClick={() => copy('play.ugeappagesmp.online', 'java')}><small>JAVA IP</small><b>{copied === 'java' ? 'Copied!' : 'play.ugeappagesmp.online'}</b><CopyIcon/></button><button onClick={() => copy('19132', 'bedrock')}><small>BEDROCK PORT</small><b>{copied === 'bedrock' ? 'Copied!' : '19132'}</b><CopyIcon/></button></div><Link className="outline-link dossier-hero-btn" to="/profile">Open Citizen Dossier & Ledger <ArrowIcon /></Link></div>;
  if (user.status === 'pending') return <div className="status-card pending"><span>⏳ APPLICATION PENDING REVIEW</span><h3>{user.mcUsername}</h3><p>Wait in the Discord <strong>#Waiting for Interview</strong> voice channel. An Admin will review your application.</p>{user.updatedAt && <small>SUBMITTED {new Date(user.updatedAt).toLocaleString()}</small>}<Link className="outline-link dossier-hero-btn" to="/profile">View Application Status & Profile <ArrowIcon /></Link></div>;
  return <div className="hero-actions"><a className="primary-btn enchant" href="https://apply.ugeappagesmp.online">✦ Apply for whitelist now <ArrowIcon /></a><Link className="outline-link" to="/journey">Our Journey <ArrowIcon /></Link><Link className="ghost-link" to="/profile">My Profile <ArrowIcon /></Link></div>;
}
